import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Sparkles,
  Truck,
  Clock,
  MapPin,
  Boxes,
  Tag,
  Layers,
  Recycle,
  Shirt,
  Shield,
  ListChecks,
  Archive,
  BookOpen,
  Utensils,
  LifeBuoy,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";
import { getYvelinesCityPageBySlug } from "@/data/yvelinesCitiesContent";

const cityNameToSlug = (name) =>
  name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/'/g, "")
    .replace(/\s+/g, "-");

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison haut de gamme ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison haut de gamme
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Notre service de livraison de cartons haut de gamme à
            domicile {city.aCity}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Parce que charger des dizaines de cartons volumineux dans
            une voiture personnelle est une contrainte logistique
            majeure, notre service de proximité prend tout en charge
            directement chez vous.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Une dépose sur-mesure",
              body: "Nous livrons l'intégralité de vos fournitures à plat sur le pas de votre porte à Maisons-Laffitte, ainsi que dans les communes voisines comme Sartrouville, Le Mesnil-le-Roi, Saint-Germain-en-Laye et Houilles.",
              Icon: MapPin,
            },
            {
              title: "La garantie de la ponctualité",
              body: "Nous fixons ensemble un créneau horaire précis pour respecter scrupuleusement votre emploi du temps et vous permettre de commencer vos cartons sans attendre.",
              Icon: Clock,
            },
            {
              title: "Un matériel impeccable",
              body: "Vos cartons vous sont remis dans un état de propreté irréprochable, parfaitement secs et protégés des intempéries, prêts pour une mise en boîte immédiate.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Bon Plan Éco-Responsable ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Bon plan éco-responsable
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Bon Plan Éco-Responsable : nos cartons reconditionnés à
              moins 40 %
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Alliez économies majeures et démarche éco-citoyenne ! Pour
              les déménagements à fort volume {city.aCity}, nous
              proposons une gamme exclusive de cartons professionnels
              reconditionnés ayant servi une seule et unique fois.
              Triés sur le volet et inspectés manuellement par nos
              équipes, ces emballages conservent l&apos;intégralité de
              leur rigidité structurelle et leur double cannelure
              d&apos;origine.
            </p>
            <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              En optant pour cette solution, vous bénéficiez d&apos;une
              remise exceptionnelle de 40 % par rapport au prix du neuf,
              tout en réduisant drastiquement l&apos;empreinte carbone
              de votre déménagement grâce à un circuit court de
              l&apos;économie circulaire.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/cartons-demenagement"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
                data-testid="city-maisons-laffitte-cta-reconditionnes"
              >
                <Recycle className="h-4 w-4" strokeWidth={2.6} />
                Voir nos cartons reconditionnés
                <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
              </Link>
              <Link
                to="/kits-cartons"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
                data-testid="city-maisons-laffitte-cta-kits"
              >
                <Boxes className="h-4 w-4" strokeWidth={2.6} />
                Découvrir nos kits complets
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7 space-y-4">
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Recycle className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                  Économie circulaire
                </h3>
              </div>
              <div className="space-y-2.5 text-[14px] text-[#3a4856]">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  Cartons utilisés 1 seule fois
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  Inspection manuelle systématique
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  Double cannelure conservée
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  Remise exceptionnelle de 40 %
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  Circuit court bas-carbone
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Objectif zéro pli ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Vos dressings &amp; textiles
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Objectif zéro pli : vos dressings et textiles sous haute
            protection
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Le transport des vêtements est souvent négligé, pourtant vos
            pièces de valeur méritent mieux que d&apos;être tassées au
            fond d&apos;un sac.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Cartons penderies */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Shirt className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Les cartons penderies verticaux
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Dotés d&apos;une tringle métallique intégrée, ils vous
              permettent de transférer vos costumes, robes de créateurs
              et manteaux directement sur leurs cintres. Vos vêtements
              voyagent suspendus, à l&apos;abri de la poussière, de
              l&apos;écrasement et des faux plis, prêts à être
              réinstallés immédiatement dans votre future armoire.
            </p>
            <div className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Tringle métallique intégrée
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Idéal robes de créateurs &amp; costumes
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Aucun écrasement ni faux pli
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Réinstallation immédiate
              </div>
            </div>
          </div>

          {/* Cartons standards linge de maison */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Shield className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Les cartons standards pour le linge de maison
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Pour vos draps, couettes et rideaux, nos grands formats
              légers offrent un volume optimal. Associés à nos housses
              de protection étanches, ils garantissent que vos textiles
              resteront parfaitement propres et frais durant toute la
              durée du transport.
            </p>
            <div className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Grands formats légers
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Volume optimal pour draps &amp; couettes
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Housses de protection étanches
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Textiles propres et frais
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-maisons-laffitte-cta-shop"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir toute la gamme
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-maisons-laffitte-cta-accessoires"
          >
            <Tag className="h-4 w-4" strokeWidth={2.6} />
            Adhésifs &amp; protections
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — Check-list méthode (timeline) ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Méthode chronologique
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            La check-list ultime : emballez vos pièces avec méthode
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour éviter de vous laisser déborder les derniers jours,
            suivez ce plan d&apos;attaque chronologique pour organiser
            la mise en boîte de votre habitation.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {[
            {
              step: "J-30",
              title: "Espaces de stockage et hors-quotidien",
              body: "Commencez par le garage, la cave ou les greniers, ainsi que les dressings d'hiver/été selon la saison. Utilisez nos cartons à double cannelure renforcés pour les objets lourds ou les outils coupants.",
              Icon: Archive,
            },
            {
              step: "J-15",
              title: "Culture et décoration",
              body: "Emballez vos bibliothèques (uniquement dans des petits cartons spécial livres pour soulager les bras), vos collections, les cadres et les bibelots. Protégez chaque élément sensible avec deux couches de film à bulles haute densité.",
              Icon: BookOpen,
            },
            {
              step: "J-7",
              title: "Cuisine et arts de la table",
              body: "C'est le moment de basculer sur nos cartons de vaisselle à croisillons pour vos assiettes et verres fins. Laissez uniquement le strict minimum pour les repas des derniers jours.",
              Icon: Utensils,
            },
            {
              step: "J-2",
              title: "Le kit de survie « Jour J »",
              body: "Préparez un carton spécifique que vous garderez avec vous : chargeurs, documents administratifs du nouveau logement, brosses à dents, vêtements de rechange pour la première nuit et outillage de base (tournevis, cutter) pour le remontage des meubles.",
              Icon: LifeBuoy,
            },
          ].map(({ step, title, body, Icon }) => (
            <div
              key={step}
              className="bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6 flex flex-col"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#237cb5]">
                  {step}
                </span>
              </div>
              <h3 className="mt-3 font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                {title}
              </h3>
              <p className="mt-2.5 text-[13.5px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-maisons-laffitte-cta-fermeture"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Adhésifs, film &amp; papier kraft
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/livraison"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-maisons-laffitte-cta-livraison"
          >
            <Truck className="h-4 w-4" strokeWidth={2.6} />
            Nos modalités de livraison
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 6 — Zones de livraison Yvelines ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Couverture {city.deptCode}
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Nos zones de livraison de cartons dans les Yvelines (78)
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Afin de répondre aux besoins des habitants du département,
            nous distribuons nos cartons de déménagement et accessoires
            d&apos;emballage dans de nombreuses communes des Yvelines.
            Notre service permet de recevoir facilement les fournitures
            indispensables à l&apos;organisation de votre déménagement,
            directement à l&apos;adresse de votre choix.
          </p>
          <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Consultez ci-dessous les villes du 78 dans lesquelles nos
            livraisons sont régulièrement effectuées à proximité de
            {" "}{city.name}&nbsp;:
          </p>
        </div>

        <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2.5 text-[15px] md:text-base">
          {city.nearbyCities.map((name) => {
            const idSlug = cityNameToSlug(name);
            const target = getYvelinesCityPageBySlug(idSlug);
            return (
              <li key={name}>
                {target ? (
                  <Link
                    to={`/carton-demenagement-${target.slug}`}
                    className="inline-flex items-center font-semibold text-[#237cb5] underline underline-offset-4 decoration-[#237cb5]/40 hover:decoration-[#237cb5] hover:text-[#0f1d2b] transition-colors"
                    data-testid={`city-maisons-laffitte-nearby-${idSlug}`}
                  >
                    Carton déménagement {name}
                  </Link>
                ) : (
                  <span
                    className="inline-flex items-center font-semibold text-[#5b6b7a]"
                    data-testid={`city-maisons-laffitte-nearby-${idSlug}`}
                  >
                    Carton déménagement {name}
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        <div className="mt-8">
          <Link
            to={`/cartons-demenagement/${city.deptSlug}`}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#EEF3F7] border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-maisons-laffitte-cta-back-dept"
          >
            <Package className="h-4 w-4" strokeWidth={2.6} />
            Voir la page {city.deptName} ({city.deptCode})
          </Link>
        </div>
      </div>
    </section>
  </>
);

export default { Sections };
